/**
 * Drive store.
 *
 * Tracks every removable drive the native `drives` module has seen, which
 * one (if any) is the active Seufic Drive, and drives the plug-in/plug-out
 * lifecycle: granting filesystem scope, validating/self-healing the SEUFIC/
 * layout, and — via `services/drives/paths.ts`'s `setActiveDriveRoot` —
 * telling every storage provider where to read/write. See
 * `DRIVE_ARCHITECTURE.md` for the full lifecycle diagram.
 */
import { create } from "zustand";
import type { DriveInfo, DriveRecord, DriveStatus } from "@/types";
import {
  allowDriveScope,
  ensureDriveLayout,
  getActiveRoot,
  listRemovableDrives,
  onDriveChanged,
  onDriveConnected,
  onDriveDisconnected,
  seuficRoot,
  setActiveDriveRoot,
  validateDrive,
} from "@/services/drives";
import { logger } from "@/services/logger";
import { isTauri } from "@/utils";
import { useAppStore } from "./appStore";

const log = logger.scope("drive-store");

interface DriveState {
  status: DriveStatus;
  /** Every drive seen so far, keyed by mount point. */
  drives: Record<string, DriveRecord>;
  activeMountPoint: string | null;
  error: string | null;
  initialized: boolean;

  init: () => Promise<void>;
  selectDrive: (mountPoint: string) => void;
  disconnect: () => void;
}

function pickNextValidDrive(drives: Record<string, DriveRecord>): DriveRecord | null {
  return Object.values(drives).find((d) => d.valid) ?? null;
}

async function activateLocalFallback(): Promise<void> {
  setActiveDriveRoot({ kind: "local" });
  if (isTauri()) {
    try {
      await ensureDriveLayout(await getActiveRoot());
    } catch (err) {
      log.warn("Failed to ensure local storage layout", err);
    }
  }
}

async function tryConnect(info: DriveInfo, announce: boolean): Promise<void> {
  const state = useDriveStore.getState();
  useDriveStore.setState({
    drives: { ...state.drives, [info.mountPoint]: { info, valid: false, marker: null } },
  });

  const root = seuficRoot(info.mountPoint);
  try {
    await allowDriveScope(root);
    const marker = await validateDrive(root);
    useDriveStore.setState((s) => ({
      drives: { ...s.drives, [info.mountPoint]: { info, valid: true, marker } },
    }));

    if (!useDriveStore.getState().activeMountPoint) {
      setActiveDriveRoot({ kind: "drive", mountPoint: info.mountPoint });
      useDriveStore.setState({ activeMountPoint: info.mountPoint, status: "connected", error: null });
      if (announce) {
        useAppStore.getState().addToast({
          title: "Seufic Drive connected",
          description: info.name || info.mountPoint,
          variant: "success",
        });
      }
    }
  } catch (err) {
    log.error("Failed to validate drive", { mountPoint: info.mountPoint, err });
    useDriveStore.setState((s) => ({
      drives: { ...s.drives, [info.mountPoint]: { info, valid: false, marker: null } },
      status: s.activeMountPoint ? s.status : "invalid",
      error: err instanceof Error ? err.message : "Couldn't set up this drive.",
    }));
  }
}

async function handleDisconnected(mountPoint: string): Promise<void> {
  const state = useDriveStore.getState();
  const wasActive = state.activeMountPoint === mountPoint;
  const drives = { ...state.drives };
  delete drives[mountPoint];
  useDriveStore.setState({ drives });

  if (!wasActive) return;

  const next = pickNextValidDrive(drives);
  if (next) {
    setActiveDriveRoot({ kind: "drive", mountPoint: next.info.mountPoint });
    useDriveStore.setState({ activeMountPoint: next.info.mountPoint, status: "connected" });
    useAppStore.getState().addToast({
      title: "Switched to another Seufic Drive",
      description: next.info.name || next.info.mountPoint,
      variant: "default",
    });
  } else {
    await activateLocalFallback();
    useDriveStore.setState({ activeMountPoint: null, status: "missing" });
    useAppStore.getState().addToast({
      title: "Seufic Drive disconnected",
      description: "Falling back to local storage until it's reconnected.",
      variant: "warning",
    });
  }
}

export const useDriveStore = create<DriveState>()((set, get) => ({
  status: "disconnected",
  drives: {},
  activeMountPoint: null,
  error: null,
  initialized: false,

  init: async () => {
    if (get().initialized) return;
    set({ initialized: true });

    await activateLocalFallback();

    if (!isTauri()) return;

    void onDriveConnected((info) => void tryConnect(info, true));
    void onDriveChanged((info) => {
      set((s) => {
        const existing = s.drives[info.mountPoint];
        if (!existing) return s;
        return { drives: { ...s.drives, [info.mountPoint]: { ...existing, info } } };
      });
    });
    void onDriveDisconnected((mountPoint) => void handleDisconnected(mountPoint));

    try {
      const initial = await listRemovableDrives();
      if (initial.length > 0) set({ status: "connecting" });
      for (const info of initial) {
        await tryConnect(info, false);
      }
      if (!get().activeMountPoint && initial.length === 0) {
        set({ status: "disconnected" });
      }
    } catch (err) {
      log.error("Failed to list removable drives", err);
    }
  },

  selectDrive: (mountPoint) => {
    const record = get().drives[mountPoint];
    if (!record?.valid) return;
    setActiveDriveRoot({ kind: "drive", mountPoint });
    set({ activeMountPoint: mountPoint, status: "connected", error: null });
  },

  disconnect: () => {
    void activateLocalFallback();
    set({ activeMountPoint: null, status: "disconnected" });
  },
}));
